import _ from 'lodash';
import { encode as vlqEncode } from 'vlq';

// navigateCtor is a constructor for the navigator. It returns commonly called
// functions that allow the caller to navigate through the routes. It can be
// embedded in the router as follows:
//   Router.Navigate = navigateCtor(Router);
// Then, in your components, you can access $router to do navigation e.g.
//   <a @click="$router.Navigate.Reservable({ reservableId: resource.id })"></a>
// This cuts down heavily on boilerplate relating to using router link with
// complicated parameters.
export const navigateCtor = (router) => ({
  AdminFormEdit: (formId, siteId) => {
    if (!formId) {
      throw Error('router nav failure AdminFormEdit: missing formid');
    }
    if (!siteId) {
      throw Error('router nav failure AdminFormEdit: missing siteid');
    }

    const route = {
      name: 'siteeditform',
      params: {
        formId,
        siteId,
      },
    };

    router.push(route);
  },

  AdminMassMessage: (siteId, query = {}) => {
    if (!siteId) {
      throw Error('router nav failure AdminFormEdit: missing siteid');
    }

    const route = {
      name: 'sitemessaging',
      params: {
        siteId,
      },
      query,
    };

    router.push(route);
  },

  AdminPortalUserTraining: (portalId, trainingId) => {
    if (!portalId) {
      throw Error('router nav failure AdminPortalUserTraining: ' +
        'missing portalId');
    }

    const route = {
      name: 'portaluserstraining',
      params: {
        portalId,
      },
    };

    if (trainingId) {
      route.query = {};
      route.query.training = trainingId;
    }

    router.push(route);
  },

  // AdminPortalUserTrainingTypes navigates to the site user training
  // types administration.
  AdminPortalUserTrainingTypes: (portalId) => {
    if (!portalId) {
      throw Error('router nav failure AdminPortalUserTrainingTypes: ' +
        'missing portalid');
    }

    const route = {
      name: 'portaltraining',
      params: {
        portalId,
      },
    };

    router.push(route);
  },

  // AdminReservables goes to the table of reservables in administration.
  AdminReservables: (siteId, opts) => {
    if (!siteId) {
      throw Error('router nav failure AdminReservables: missing siteid');
    }

    let route = {
      name: 'siteresources',
      params: {
        siteId,
      },
    };

    if (opts) {
      if (opts.query) {
        route = {
          query: opts.query,
          ...route,
        };
      }
    }

    router.push(route);
  },

  // AdminReservableEdit navigates to the edit reservable page. It has the
  // optional field name in case you want to navigate to a named child of this
  // route.
  AdminReservableEdit: (reservableId, siteId, name, opts) => {
    if (!reservableId) {
      throw Error('router nav failure AdminReservableEdit: missing resvid');
    }
    if (!siteId) {
      throw Error('router nav failure AdminReservableEdit: missing siteid');
    }

    let nameLocal = 'resourceeditdetailsgeneral';
    if (name) nameLocal = name;
    let route = {
      name: nameLocal,
      params: {
        reservableId,
        siteId,
      },
    };

    if (opts) {
      if (opts.query) {
        route = {
          query: opts.query,
          ...route,
        };
      }
    }

    router.push(route);
  },

  AdminScriptEdit: (scriptId, siteId) => {
    if (!scriptId) {
      throw Error('router nav failure AdminScriptEdit: missing formid');
    }
    if (!siteId) {
      throw Error('router nav failure AdminScriptEdit: missing siteid');
    }

    const route = {
      name: 'siteeditscript',
      params: {
        scriptId,
        siteId,
      },
    };

    router.push(route);
  },

  AdminSiteInvoices: (siteId) => {
    if (!siteId) {
      throw Error('router nav failure AdminSiteInvoices: missing siteid');
    }

    const route = {
      name: 'site-invoices',
      params: {
        siteId,
      },
    };

    router.push(route);
  },

  AdminSiteInvoicesFromReservations: (siteId, reservations) => {
    if (!siteId) {
      throw Error('router nav failure AdminSiteInvoicesFromReservations: ' +
        'missing siteid');
    }
    const route = {
      name: 'site-invoices',
      params: {
        siteId,
      },
      query: {
        from_reservation_ids: reservations,
      },
    };

    router.push(route);
  },

  // AdminSiteReportsUsage navigates to site reports for usage. It optionally
  // accepts variables to transmit to the filters through query parameters.
  AdminSiteReportsUsage: (siteId, userId, reservableId) => {
    if (!siteId) {
      throw Error('router nav failure AdminSiteReportsUsage: ' +
        'missing siteid');
    }

    const route = {
      name: 'siteusage',
      params: {
        siteId,
      },
    };

    if (userId || reservableId) route.query = {};
    if (userId) {
      route.query.user = userId;
    }
    if (reservableId) {
      route.query.resource = reservableId;
    }

    router.push(route);
  },

  // AdminSiteReservables navigates to the site reservables administration.
  AdminSiteReservables: (siteId, add) => {
    if (!siteId) {
      throw Error('router nav failure AdminSiteReservables: missing siteid');
    }

    const route = {
      name: 'siteresources',
      params: {
        siteId,
      },
      query: {
        add,
      },
    };

    router.push(route);
  },

  AdminSiteScripts: (siteId) => {
    if (!siteId) {
      throw Error('router nav failure AdminSiteScripts: missing siteid');
    }
    const route = {
      name: 'resourcescripts',
      params: {
        siteId,
      },
    };

    router.push(route);
  },

  // AdminSiteSettings navigates to the site settings page. It has the
  // optional field name in case you want to navigate to a named child of this
  // route.
  AdminSiteSettings: (siteId, tab) => {
    if (!siteId) {
      throw Error('router nav failure AdminSiteSettings: missing siteid');
    }

    const query = {};
    const params = {
      siteId,
    };
    if (tab) {
      query.tab = tab;
    }
    const route = {
      name: 'sitesettings',
      params,
      query,
    };

    router.push(route);
  },

  AdminSiteSubscriptions: (siteId, isEventHook) => {
    if (!siteId) {
      throw Error('router nav failure AdminSiteSubscriptions: missing siteid');
    }

    let name = 'site-subscriptions';
    if (isEventHook) name = 'site-triggers';

    const route = {
      name,
      params: {
        siteId,
      },
    };

    router.push(route);
  },

  AdminSiteUsage: (siteId, name) => {
    if (!siteId) {
      throw Error('router nav failure AdminSiteUsage: missing siteid');
    }

    let nameLocal = 'siteusagedashboard';
    if (name) nameLocal = name;
    const route = {
      name: nameLocal,
      params: {
        siteId,
      },
    };

    router.push(route);
  },

  // AdminSiteUserTraining navigates to site reports.
  AdminSiteUserTraining: (siteId, trainingId) => {
    if (!siteId) {
      throw Error('router nav failure AdminSiteUserTraining: ' +
        'missing siteid');
    }

    const route = {
      name: 'siteuserstraining',
      params: {
        siteId,
      },
    };

    if (trainingId) {
      route.query = {};
      route.query.training = trainingId;
    }

    router.push(route);
  },

  // AdminSiteUserTrainingTypes navigates to the site user training
  // types administration.
  AdminSiteUserTrainingTypes: (siteId) => {
    if (!siteId) {
      throw Error('router nav failure AdminSiteUserTrainingTypes: ' +
        'missing siteid');
    }

    const route = {
      name: 'sitetraining',
      params: {
        siteId,
      },
    };

    router.push(route);
  },

  // AdminSiteUsers navigates to the site users administration.
  AdminSiteUsers: (siteId) => {
    if (!siteId) {
      throw Error('router nav failure AdminSiteUsers: missing siteid');
    }

    const route = {
      name: 'siteusers',
      params: {
        siteId,
      },
    };

    router.push(route);
  },
  toSiteRoute: (siteId, routeName) => {
    if (!siteId) {
      throw Error('router nav failure AdminSiteUsers: missing siteid');
    }
    if (!routeName) {
      throw Error('router nav failure toSiteRoute: missing routeName');
    }
    const route = {
      name: routeName,
      params: {
        siteId,
      },
    };

    router.push(route);
  },
  // Back redirects the router to the previous page.
  Back: () => {
    router.go(-1);
  },

  CreateSite: (parentId) => {
    if (!parentId) {
      router.push('/createsite');
    } else {
      router.push({ path: '/createsite', query: { parent_id: parentId } });
    }
  },

  CreateSiteInPortal: (portalId) => {
    router.push({ path: '/createsite', query: { portal_id: portalId } });
  },

  // Dashboard sends the user to the dashboard.
  Dashboard: (query) => {
    const route = {
      name: 'dashboard',
      query,
    };
    router.push(route);
  },

  // History sends the user to their history.
  History: (queryArg) => {
    const route = {
      name: 'history',
      query: {
        ...queryArg,
      },
    };

    router.push(route);
  },

  // Invoice goes a specific invoice to view and/or edit.
  Invoice: (invoiceId, siteAdminId, newTab) => {
    const route = {
      name: 'invoice',
      params: {
        invoiceId,
      },
    };

    if (siteAdminId) {
      route.name = 'siteadmin-invoice';
      route.params.siteId = siteAdminId;
    }

    if (newTab) {
      const url = router.resolve(route);
      window.open(url.href, '_blank');
    } else {
      router.push(route);
    }
  },

  // Subscription goes a specific subscription to view and/or edit.
  Subscription: (subscriptionId, siteId, isTrigger) => {
    let name = 'subscription';
    if (isTrigger) name = 'trigger';
    const route = {
      name,
      params: {
        subscriptionId,
      },
    };

    if (siteId) {
      route.name = 'siteadmin-subscription';
      if (isTrigger) route.name = 'siteadmin-trigger';
      route.params.siteId = siteId;
    }

    router.push(route);
  },

  // Login sends the user to the login page.
  Login: () => {
    router.push('/login');
  },

  // LoginWithTheme sends the user to the whitelabelled login page.
  LoginWithTheme: (theme) => {
    router.push({ path: '/login', query: { theme } });
  },

  // Memberships sends the user to the memberships page.
  Memberships: () => {
    router.push('/memberships');
  },

  // MembershipsSearch sends the user to the memberships search page.
  MembershipsSearch: () => {
    router.push({ path: '/memberships', query: { search: 1 } });
  },

  ProjectDashboard: (projectId) => {
    const route = {
      name: 'projectDashboard',
      params: {
        projectId,
      },
    };
    router.push(route);
  },

  ProjectFiles: (siteId, projectId) => {
    const route = {
      name: 'projectFiles',
      params: {
        projectId,
        siteId,
      },
    };
    router.push(route);
  },

  ProjectHistory: (siteId, projectId) => {
    const route = {
      name: 'projectHistory',
      params: {
        projectId,
        siteId,
      },
    };
    router.push(route);
  },

  Projects: () => {
    router.push('/projects');
  },

  ProjectUsers: (siteId, projectId) => {
    const route = {
      name: 'projectUsers',
      params: {
        siteId,
        projectId,
      },
    };
    router.push(route);
  },

  // Reservable visits a reservable or requestable resource page. If the
  // resource is a requestable and createRequest is true, it will pop up the
  // create request modal on visiting. If reservationId is a string, it
  // will pop up that reservation on visiting.
  Reservable: ({
    createRequest = null,
    portalId = null,
    query = null,
    reservableId = null,
    namedUnitId = null,
    reservationId = null,
    day = null,
  }) => {
    let route;
    if (!portalId) {
      route = {
        name: 'reservable',
        params: {
          reservableId,
        },
      };
    } else {
      route = {
        name: 'reservableWithPortal',
        params: {
          reservableId,
          portalId,
        },
      };
    }
    route.query = {};
    if (createRequest) {
      route.query.createrequest = true;
    }
    if (reservationId) {
      route.query.reservation_id = reservationId;
    }
    if (namedUnitId) {
      route.query.named_unit_id = namedUnitId;
    }
    if (day) {
      route.query.day = day;
    }
    if (query && _.isPlainObject(query)) {
      route.query = {
        ...route.query,
        ...query,
      };
    }

    router.push(route);
  },

  // ReservableCalendar visits the reservable calendar page.
  ReservableCalendar: ({
    calendarDate = null,
    createReservation = false,
    dateEnd = null,
    dateStart = null,
    first = false,
    query = null,
    reservableId = null,
    namedUnitId = null,
  }) => {
    const route = {
      name: 'reservableCalendar',
      params: {
        reservableId,
      },
      query: {},
    };

    if (first) route.query.first = first;
    if (createReservation) route.query.createreservation = createReservation;
    if (calendarDate) route.query.calendardate = calendarDate;
    if (dateStart) route.query.date = dateStart;
    if (dateEnd) route.query.date_end = dateEnd;
    if (namedUnitId) route.query.named_unit_id = namedUnitId;
    if (query && _.isPlainObject(query)) {
      route.query = {
        ...route.query,
        ...query,
      };
    }

    router.push(route);
  },

  Search: () => {
    const route = {
      name: 'search',
      params: {
      },
    };

    router.push(route);
  },

  // Site goes to a site with a portal setting colours if the portal
  // is specified.
  Site: (siteId, portalId, newSite) => {
    let route;
    if (!portalId) {
      route = {
        name: 'site',
        params: {
          siteId,
        },
        query: {
          newSite,
        },
      };
    } else {
      route = {
        name: 'siteWithPortal',
        params: {
          siteId,
          portalId,
        },
        query: {
          newSite,
        },
      };
    }

    router.push(route);
  },

  // SiteForChrome goes to the chrome extension version of a site that only
  // shows resources.
  SiteForChrome: (siteId, query) => {
    const route = {
      name: 'chrome-reservation-site',
      params: {
        siteId,
      },
      meta: {
        chromeExtension: true,
      },
    };
    if (_.isPlainObject(query)) {
      route.query = query;
    }

    router.push(route);
  },

  // SiteForOutlook goes to the outlook version of a site that only shows
  // resources.
  SiteForOutlook: (siteId, query) => {
    const route = {
      name: 'outlook-reservation-site',
      params: {
        siteId,
      },
      meta: {
        outlookView: true,
      },
    };
    if (_.isPlainObject(query)) {
      route.query = query;
    }

    router.push(route);
  },

  // SiteCalendar goes to the calendar view for some site.
  SiteCalendar: (siteId, opts) => {
    const route = {
      name: 'siteCalendar',
      params: {
        siteId,
      },
    };

    if (opts) route.query = opts;

    router.push(route);
  },

  // SiteLiveMaps goes to the live maps page for a site.
  SiteLiveMaps: (siteId, opts) => {
    const route = {
      name: 'siteLiveMaps',
      params: {
        siteId,
      },
    };

    if (opts) route.query = opts;

    router.push(route);
  },

  // SiteRequests goes to the calendar view for some site.
  SiteRequests: (siteId, opts, reservableSiteOrdinalId) => {
    const route = {
      name: 'siteRequests',
      params: {
        siteId,
      },
      query: {},
    };

    if (opts) route.query = opts;

    if (reservableSiteOrdinalId) {
      route.query.resources = vlqEncode([reservableSiteOrdinalId]);
    }

    router.push(route);
  },

  SiteWithReservation: (siteId, reservationId) => {
    const route = {
      name: 'site',
      params: {
        siteId,
      },
      query: {
        reservation_id: reservationId,
      },
    };
    router.push(route);
  },

  // User goes to a user profile with a portal setting colours if the portal
  // is specified.
  User: (userId, portalId) => {
    let route;
    if (!portalId) {
      route = {
        name: 'user',
        params: {
          userId,
        },
      };
    } else {
      route = {
        name: 'userWithPortal',
        params: {
          userId,
          portalId,
        },
      };
    }

    router.push(route);
  },

  // UserCalendar goes to the calendar view for some user.
  UserCalendar: (userId) => {
    const route = {
      name: 'userCalendar',
      params: {
        userId,
      },
    };

    router.push(route);
  },
});
